import React, { useState, useEffect } from 'react';
import { db } from './firebaseConfig';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';

// Imports dos componentes MUI
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Typography, Box, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

// Imports para gerar PDF (com a forma correta de import)
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatarData = (dataString) => {
  if (!dataString || typeof dataString !== 'string' || !dataString.includes('-')) {
    return 'N/A';
  }
  const [ano, mes, dia] = dataString.split('-');
  if (!ano || !mes || !dia) return 'Data Inválida';
  return `${dia}/${mes}/${ano}`;
};

const formatarMoeda = (valor) => {
  const valorNum = parseFloat(valor);
  if (isNaN(valorNum)) {
    return 'R$ 0,00';
  }
  return valorNum.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const HistoricoHospedeModal = ({ hospede, onClose }) => {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    const fetchHistorico = async () => {
      if (!hospede || !hospede.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const reservasRef = collection(db, 'reservas');
        const q = query(
          reservasRef,
          where('hospedeId', '==', hospede.id),
          orderBy('dataCheckin', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const historicoData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setHistorico(historicoData);
      } catch (error) {
        console.error("Erro ao buscar histórico do hóspede: ", error);
        toast.error("Não foi possível carregar o histórico.");
      } finally {
        setLoading(false);
      }
    };
    fetchHistorico();
  }, [hospede]);

  const handleGerarPDF = () => {
    setLoadingPdf(true);
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.text(`Histórico de Hospedagem`, 14, 22);
      doc.setFontSize(12);
      doc.text(`Hóspede: ${hospede?.nome || 'Não informado'}`, 14, 30);
      
      const tableData = historico.map(reserva => {
        const periodo = `${formatarData(reserva.dataCheckin)} a ${formatarData(reserva.dataCheckout)}`;
        const chale = String(reserva.chaleNome || 'N/A');
        const valor = formatarMoeda(reserva.valorTotal);
        return [periodo, chale, valor];
      });

      // --- CORREÇÃO AQUI: A função é chamada passando o 'doc' como argumento ---
      autoTable(doc, {
        startY: 40,
        head: [['Período', 'Chalé', 'Valor Total']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [74, 35, 90] }, // Um roxo escuro para diferenciar
        didParseCell: function (data) {
            if (data.column.index === 2) {
                data.cell.styles.halign = 'right';
            }
        }
      });

      const nomeArquivo = `Historico-${(hospede?.nome || 'hospede').replace(/ /g, '_')}.pdf`;
      doc.save(nomeArquivo);

    } catch (error) {
      console.error("Erro detalhado ao gerar PDF do histórico:", error);
      toast.error("Ocorreu um erro ao gerar o PDF.");
    } finally {
      setLoadingPdf(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Histórico de Hospedagem
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6" gutterBottom>{hospede.nome}</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Período</TableCell>
                  <TableCell>Chalé</TableCell>
                  <TableCell align="right">Valor Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historico.length > 0 ? (
                  historico.map(reserva => (
                    <TableRow key={reserva.id}>
                      <TableCell>
                        {formatarData(reserva.dataCheckin)} a {formatarData(reserva.dataCheckout)}
                      </TableCell>
                      <TableCell>{reserva.chaleNome}</TableCell>
                      <TableCell align="right">{formatarMoeda(reserva.valorTotal)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Nenhum histórico de reserva encontrado para este hóspede.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button 
          onClick={handleGerarPDF} 
          variant="contained" 
          startIcon={loadingPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
          disabled={loadingPdf || historico.length === 0}
        >
          {loadingPdf ? 'Gerando...' : 'Gerar PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoricoHospedeModal;